'use client';

import React, { type FC } from 'react';
import { formatCreditsNumber, formatCreditsShort } from '../utils/formatters';
import { CreditsChangeFlash } from './CreditsChangeFlash';
import { FaCoins } from './icons';

interface CreditsProps {
  amount: number;
  className?: string;
  showIcon?: boolean;
  iconSize?: number | string;
  iconOnly?: boolean; // Show only icon without amount
  muted?: boolean; // Use muted gray color for icon
  compact?: boolean; // Abbreviate to k/M so big amounts fit tight columns
  /** 'baseline' sits the icon on the surrounding text's baseline instead of
   *  vertically centering it — for inline use next to prose (e.g. a heading). */
  align?: 'center' | 'baseline';
  /** Count the amount from its old value to the new one, green when it grows and red when
   *  it shrinks. Only for the user's OWN balance — prices, drop-feed values and case costs
   *  must stay still through every background refetch, so this is off by default. Requires
   *  the authed provider tree. */
  animateChange?: boolean;
}

export const Credits: FC<CreditsProps> = ({ amount, className, showIcon = true, iconSize = 12, iconOnly = false, muted = false, compact = false, align = 'center', animateChange = false }) => {
  const format = compact ? formatCreditsShort : formatCreditsNumber;

  return (
    <span className={`inline-flex ${align === 'baseline' ? 'items-baseline' : 'items-center'} gap-1 ${className || ''}`}>
      {!iconOnly &&
        (animateChange ? <CreditsChangeFlash amount={amount} format={format} /> : format(amount))}
      {showIcon && (
        <FaCoins className={`${muted ? 'text-gray-400' : 'text-amber-400'} shrink-0`} size={iconSize} />
      )}
    </span>
  );
};
